import { LoggingService } from '@app/core/services/logging/logging.service';
import { inject } from '@angular/core';
import { CanMatchFn, Route, Router, UrlSegment } from '@angular/router';
import { BookingPostModel, StayTypeSelect } from '@app/core/models';

import { BookingService } from '@app/core/services/backend/booking.service';
import { FirebaseAuthenticationService } from '@app/core/services/firebase/firebase-authentication/firebase-authentication.service';
import { PropertyService } from '@app/core/services/backend/property.service';
import { BookingRepository } from '@app/store';

export const createBookingGuard: CanMatchFn = (
  route: Route,
  segments: UrlSegment[],
): Promise<boolean> => {
  const bookingRepo = inject(BookingRepository);
  const firebaseAuthenticationService = inject(FirebaseAuthenticationService);
  const propertyService = inject(PropertyService);
  const router = inject(Router);
  const STORAGE_KEY = 'bookingFormData';
  const bookingService = inject(BookingService);
  LoggingService.log(route, segments);

  return new Promise<boolean>(async (resolve) => {
    const queryParams = new URLSearchParams(window.location.search);
    const queryParamsObj = Object.fromEntries(queryParams.entries());

    // Get stayType from URL parameter, default to 'vacation'
    const stayType =
      (queryParamsObj['st'] as StayTypeSelect) || StayTypeSelect.VACATION;

    // For vacation stays, 'to' parameter is required
    if (stayType === StayTypeSelect.VACATION && !queryParamsObj['to']) {
      resolve(false);
      return;
    }

    const propertyCode = segments[2].path;
    const user = await firebaseAuthenticationService.getCurrentUser();
    propertyService.get(propertyCode).subscribe((property) => {
      if (!property) {
        router.navigate(['/no-bookings']);
        resolve(false);
      }

      // Build booking object based on stayType
      const createBooking: BookingPostModel = {
        propertyId: property._id,
        booking: {
          numberOfGuests: {
            adults: queryParamsObj['a']
              ? Number.parseInt(queryParamsObj['a'])
              : 1,
            children: queryParamsObj['c']
              ? Number.parseInt(queryParamsObj['c'])
              : 0,
            infants: queryParamsObj['i']
              ? Number.parseInt(queryParamsObj['i'])
              : 0,
            pets: queryParamsObj['p']
              ? Number.parseInt(queryParamsObj['p'])
              : 0,
          },
          stayType: stayType,
        },
      };

      // Only add dates for vacation stays
      if (stayType === StayTypeSelect.VACATION) {
        const start = queryParamsObj['from']
          ? new Date(queryParamsObj['from'])
          : new Date();
        const end = new Date(queryParamsObj['to']);
        if (end <= start) {
          resolve(false);
          return;
        }
        createBooking.booking.start = start;
        createBooking.booking.end = end;
      }
      if (!user) {
        const dataToStore: any = {
          numberOfGuests: createBooking.booking.numberOfGuests,
          propertyCode: propertyCode,
          propertyId: createBooking.propertyId,
          stayType: stayType,
        };

        // Only store dates for vacation stays
        if (stayType === StayTypeSelect.VACATION) {
          dataToStore.start = createBooking.booking.start;
          dataToStore.end = createBooking.booking.end;
        }

        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(dataToStore));
        void router.navigate(['/login']);
        resolve(false);
        return;
      }
      createBooking.propertyId = property._id;
      bookingService.getAll().subscribe((bookings) => {
        if (bookings && bookings.length > 0) {
          let booking;

          // For residential stays, find existing residential booking for the property
          if (stayType === StayTypeSelect.RESIDENTIAL) {
            booking = bookings.find(
              (b) =>
                b.property?._id === property._id &&
                b.stayType === 'residential',
            );
          } else {
            // For vacation stays, find booking that covers the date range
            booking = bookings.find(
              (b) =>
                b.property?._id === property._id &&
                createBooking.booking.start &&
                createBooking.booking.end &&
                new Date(createBooking.booking.start) >= new Date(b.start) &&
                new Date(createBooking.booking.end) <= new Date(b.end),
            );
          }

          if (booking) {
            bookingRepo.setActiveBookingId(booking._id);
            bookingRepo.updateBookings([booking]);
            router.navigate(['/explore']);
            resolve(true);
          } else {
            propertyService
              .createBooking(createBooking)
              .subscribe((booking) => {
                const storedData = sessionStorage.getItem(STORAGE_KEY);
                if (storedData) {
                  sessionStorage.removeItem(STORAGE_KEY);
                }
                bookingRepo.updateBookings([booking]);
                bookingRepo.setActiveBookingId(booking._id);
                router.navigate(['/explore']);
                resolve(true);
              });
          }
        } else {
          propertyService.createBooking(createBooking).subscribe((booking) => {
            const storedData = sessionStorage.getItem(STORAGE_KEY);
            if (storedData) {
              sessionStorage.removeItem(STORAGE_KEY);
            }
            bookingRepo.updateBookings([booking]);
            bookingRepo.setActiveBookingId(booking._id);
            router.navigate(['/explore']);
            resolve(true);
          });
        }
      });
    });
  });
};
